import { createSlice } from '@reduxjs/toolkit';

/**
 * Who is signed in.
 *
 * `activeUser` and `business` start as "" rather than null because screens
 * around the app destructure them (`const { business } = useSelector(s =>
 * s.login.activeUser)`); an empty string destructures to undefined, where
 * null would throw.
 *
 * `notice` is why the last session ended, so the login screen can say
 * something useful instead of just reappearing:
 *   {kind: 'rejected'}                         — the server refused the token
 *   {kind: 'printed', orderNumber, name}       — a waiter's order went to the printer
 */
export const emptyLoginState = {
  isLogin: false,
  activeUser: '',
  business: '',
  token: '',
  mySocketId: '',
  notice: null,
};

export const loginSlice = createSlice({
  name: 'login',
  initialState: { ...emptyLoginState },
  reducers: {
    setIsLogin: (state, action) => {
      state.isLogin = action.payload;
    },
    setActiveUser: (state, action) => {
      state.activeUser = action.payload;
    },
    setBusiness: (state, action) => {
      state.business = action.payload;
    },
    setToken: (state, action) => {
      state.token = action.payload;
    },
    setSocketId: (state, action) => {
      state.mySocketId = action.payload;
    },

    /**
     * One action for a successful sign-in, so the four writes can never land
     * half-applied and leave a user with a token but no business.
     */
    signedIn: (state, action) => {
      const { user, token } = action.payload;
      state.isLogin = true;
      state.activeUser = user;
      state.business = user?.business;
      state.token = token;
      state.notice = null;
    },

    /**
     * Every way a session ends except a plain Logout, each carrying the reason
     * the login screen should show.
     */
    sessionEnded: (state, action) => ({
      ...emptyLoginState,
      notice: action.payload || null,
    }),

    clearNotice: (state) => {
      state.notice = null;
    },

    logout: () => ({ ...emptyLoginState }),
  },
});

export const {
  setIsLogin,
  setActiveUser,
  setBusiness,
  setToken,
  setSocketId,
  signedIn,
  sessionEnded,
  clearNotice,
  logout,
} = loginSlice.actions;

/** Ready-made notices, so no caller has to remember the shape. */
export const rejectedNotice = () => sessionEnded({ kind: 'rejected' });
export const printedNotice = (orderNumber, name) =>
  sessionEnded({ kind: 'printed', orderNumber, name });

export default loginSlice.reducer;
